import { Button } from '@brightdrop/bd-ui';
import {
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import SendArrowIcon from '../icons/SendArrowIcon';
import useStyles from './EditLastQueryForm.styles';

const CANCEL_BUTTON_WIDTH = '117px';
const CANCEL_BUTTON_SX = (theme: Theme) => ({
  borderRadius: theme.new?.shape.borderRadius.small ?? '0.5rem',
  textTransform: 'none' as const,
});

/** Props for the inline latest-query editor. */
export type EditLastQueryFormProps = {
  messageId: string;
  initialValue: string;
  isSubmitting: boolean;
  isSubmitDisabled?: boolean;
  onCancel: () => void;
  onSubmit: (content: string) => Promise<boolean>;
};

/**
 * Prefilled edit form that creates a new conversation branch on submit.
 * Submitting an empty query cancels the edit instead, restoring the
 * original query.
 */
function EditLastQueryForm({
  messageId,
  initialValue,
  isSubmitting,
  isSubmitDisabled = false,
  onCancel,
  onSubmit,
}: EditLastQueryFormProps) {
  const { classes } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const [value, setValue] = useState(initialValue);
  const trimmedValue = value.trim();
  const isEmpty = trimmedValue.length === 0;
  const canSubmit = !isSubmitting && !isSubmitDisabled;
  const warningId = `ai-sidekick-edit-warning-${messageId}`;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValue(event.target.value);
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) {
      return;
    }
    if (isEmpty) {
      onCancel();
      return;
    }
    await onSubmit(trimmedValue);
  }, [canSubmit, isEmpty, onCancel, onSubmit, trimmedValue]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing
      ) {
        event.preventDefault();
        void handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <Box
      component="section"
      className={classes.root}
      data-testid={`ai-sidekick-edit-form-${messageId}`}
    >
      <Box className={classes.fieldGroup}>
        <TextField
          className={classes.textfield}
          multiline
          minRows={1}
          maxRows={4}
          autoFocus
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isSubmitting}
          slotProps={{
            input: {
              sx: { padding: 0 },
            },
            htmlInput: {
              'aria-label': t('chat.edit.textareaAriaLabel'),
              'aria-describedby': warningId,
              'data-testid': `ai-sidekick-edit-input-${messageId}`,
              sx: {
                paddingTop: 1.5,
                paddingRight: 1.5,
                paddingBottom: 1.5,
                paddingLeft: 2,
              },
            },
          }}
        />
        <Typography
          id={warningId}
          component="p"
          variant="caption_regular"
          className={classes.warning}
        >
          {t('chat.edit.warning')}
        </Typography>
      </Box>
      <Box className={classes.actions}>
        <Button
          variant="tertiary"
          size="medium"
          width={CANCEL_BUTTON_WIDTH}
          sx={CANCEL_BUTTON_SX}
          onClick={onCancel}
          disabled={isSubmitting}
          data-testid={`ai-sidekick-edit-cancel-${messageId}`}
        >
          {t('chat.edit.cancelLabel')}
        </Button>
        <IconButton
          className={classes.submitButton}
          onClick={handleSubmit}
          disabled={!canSubmit}
          aria-label={
            isSubmitting
              ? t('chat.edit.submitLoadingAriaLabel')
              : isEmpty
                ? t('chat.edit.restoreAriaLabel')
                : t('chat.edit.submitLabel')
          }
          data-testid={`ai-sidekick-edit-submit-${messageId}`}
        >
          {isSubmitting ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <SendArrowIcon size={16} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
}

export default EditLastQueryForm;
